import { Link, useForm } from "@inertiajs/react"

import type { SharedProps } from "@/types"
import { AuthCard } from "@/components/auth-card"
import { AuthShell } from "@/components/auth-shell"
import { AuthStatus } from "@/components/auth-status"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"

type EmailSettingsPageProps = SharedProps & {
  errorSummary: string[]
  paths: {
    resendVerification: string
    root: string
    updateEmail: string
  }
  values: {
    email?: string
  }
  verified: boolean
}

export default function EmailSettingsPage({
  errorSummary,
  flash,
  paths,
  values,
  verified,
}: EmailSettingsPageProps) {
  const form = useForm({
    email: values.email ?? "",
    password_challenge: "",
  })
  const resend = useForm({})

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    form.patch(paths.updateEmail, {
      onFinish: () => form.reset("password_challenge"),
    })
  }

  function resendVerification() {
    resend.post(paths.resendVerification)
  }

  const heroCopy = verified
    ? {
        heroTitle: "Update your account email.",
        heroDescription: "We'll send a confirmation to your new address before switching.",
        heroPoints: [
          "Your current password is required to confirm the change.",
          "The new address won't be active until you verify it.",
          "You can still sign in with your current email until then.",
        ] as const,
      }
    : {
        heroTitle: "Almost there — verify your email.",
        heroDescription: "Confirm your address to secure your account and unlock full access.",
        heroPoints: [
          "Check the inbox of the email you signed up with.",
          "The verification link expires — request a new one here if needed.",
          "You can correct the address here if the original was entered incorrectly.",
        ] as const,
      }

  return (
    <AuthShell
      heroDescription={heroCopy.heroDescription}
      heroPoints={heroCopy.heroPoints}
      heroTitle={heroCopy.heroTitle}
      pageTitle={verified ? "Change email" : "Verify email"}
    >
      <div className="flex flex-col gap-4">
        <AuthStatus errorSummary={errorSummary} flash={flash} />
        {!verified && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-4 text-sm text-amber-950 shadow-sm">
            <p className="font-semibold">Verify your email</p>
            <p className="mt-1 leading-6 text-amber-900/90">
              We sent a verification email to your current address. Check that
              inbox and follow the link to confirm ownership.
            </p>
            <Button
              className="mt-4 w-full"
              disabled={resend.processing}
              onClick={resendVerification}
              type="button"
              variant="outline"
            >
              {resend.processing ? "Sending..." : "Re-send verification email"}
            </Button>
          </div>
        )}
        <AuthCard
          description={
            verified
              ? "Use your current password to confirm the change."
              : "You can update the address here if the original one was incorrect."
          }
          eyebrow="Account email"
          title={verified ? "Change email" : "Update your email"}
        >
          <form onSubmit={submit}>
            <FieldGroup>
              <Field data-invalid={!!form.errors.email}>
                <FieldLabel htmlFor="email">New email</FieldLabel>
                <Input
                  aria-invalid={!!form.errors.email}
                  autoComplete="email"
                  id="email"
                  name="email"
                  onChange={(event) => form.setData("email", event.target.value)}
                  placeholder="name@example.com"
                  required
                  type="email"
                  value={form.data.email}
                />
                <FieldError>{form.errors.email}</FieldError>
              </Field>
              <Field data-invalid={!!form.errors.password_challenge}>
                <FieldLabel htmlFor="password_challenge">
                  Current password
                </FieldLabel>
                <Input
                  aria-invalid={!!form.errors.password_challenge}
                  autoComplete="current-password"
                  id="password_challenge"
                  name="password_challenge"
                  onChange={(event) =>
                    form.setData("password_challenge", event.target.value)
                  }
                  required
                  type="password"
                  value={form.data.password_challenge}
                />
                <FieldError>{form.errors.password_challenge}</FieldError>
              </Field>
              <Field>
                <Button className="w-full" disabled={form.processing} type="submit">
                  {form.processing ? "Saving..." : "Save changes"}
                </Button>
                <FieldDescription className="text-center">
                  <Link href={paths.root}>Back to home</Link>
                </FieldDescription>
              </Field>
            </FieldGroup>
          </form>
        </AuthCard>
      </div>
    </AuthShell>
  )
}
